import { Button, Input } from '@heroui/react'
import { useDeleteUser } from '@qse/api'
import type { ModalComponentProps } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import { formatStorageValue } from '@qse/utils'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

interface DeleteUserConfirmationData {
  data: {
    user: {
      id: string
      firstName: string
      lastName: string
      email: string
      quotaSummary?: {
        storage?: {
          used: number
        }
      }
    }
    onSuccess?: () => void
  }
}

export default function DeleteUserConfirmationModal(props: ModalComponentProps) {
  const { onClose } = props
  const { data } = useActiveModelStore()
  const [confirmText, setConfirmText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const isDeletingRef = useRef(false)

  const modalData = data as DeleteUserConfirmationData
  const { mutateAsync: deleteUser } = useDeleteUser()

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      return
    }

    // Prevent double-submit: check ref synchronously before any async operations
    if (isDeletingRef.current) {
      return
    }

    try {
      // Set both ref (synchronous) and state (for UI)
      isDeletingRef.current = true
      setIsDeleting(true)
      await deleteUser(modalData.data.user.id)

      // Clear confirmation text immediately to disable button
      // This prevents any clicks during the brief moment before modal closes
      setConfirmText('')

      toast.success('User deleted successfully', {
        description: `${modalData.data.user.firstName} ${modalData.data.user.lastName} has been deleted and their data has been scheduled for cleanup.`,
      })

      if (modalData.data.onSuccess) {
        modalData.data.onSuccess()
      }

      // Close modal using the proper method
      // Note: We don't reset deleting states on success to keep button disabled until modal closes
      onClose()
    } catch (error: any) {
      console.error('Failed to delete user:', error)

      // Only reset states on error to allow retry
      isDeletingRef.current = false
      setIsDeleting(false)

      // Handle specific error cases
      if (error?.response?.data?.message?.includes('Primary Tenant Admin')) {
        toast.error('Cannot Delete Primary Tenant Admin', {
          description:
            'Primary Tenant Admin cannot be deleted. Please transfer the admin role first, then delete the user.',
        })
      } else {
        toast.error('Failed to delete user', {
          description:
            error?.response?.data?.message ||
            'An error occurred while deleting the user. Please try again.',
        })
      }
    }
  }

  const handleCancel = () => {
    onClose()
  }

  const storageUsed = modalData?.data?.user?.quotaSummary?.storage?.used || 0
  const isDeleteEnabled = confirmText === 'DELETE'

  return (
    <div className="p-6">
      <div className="flex flex-col items-center text-center mb-6">
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          This will permanently delete the user and all their data. This action cannot be undone.
        </p>
      </div>

      {/* User Details */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-gray-900 dark:text-white mb-3">User Details:</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Name:</span>
            <span className="text-gray-900 dark:text-white font-medium">
              {modalData?.data?.user?.firstName} {modalData?.data?.user?.lastName}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Email:</span>
            <span className="text-gray-900 dark:text-white font-medium">
              {modalData?.data?.user?.email}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Storage Used:</span>
            <span className="text-gray-900 dark:text-white font-medium">
              {formatStorageValue(storageUsed)}
            </span>
          </div>
        </div>
      </div>

      {/* Confirmation Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
          To proceed, type <span className="font-bold text-red-500">DELETE</span> below:
        </label>
        <Input
          isDisabled={isDeleting}
          placeholder="Type DELETE to confirm"
          value={confirmText}
          variant="bordered"
          classNames={{
            input: [
              'bg-transparent',
              'text-gray-900 dark:text-white',
              'placeholder:text-gray-500 dark:placeholder:text-gray-400',
            ],
            inputWrapper: [
              'border-gray-300 dark:border-gray-600',
              'hover:border-gray-400 dark:hover:border-gray-500',
              'focus-within:!border-red-500 dark:focus-within:!border-red-400',
            ],
          }}
          onChange={e => setConfirmText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && confirmText === 'DELETE') {
              e.preventDefault()
              handleDelete()
            }
          }}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <Button isDisabled={isDeleting} variant="light" onPress={handleCancel}>
          Cancel
        </Button>
        <Button
          color="danger"
          isDisabled={!isDeleteEnabled || isDeleting}
          isLoading={isDeleting}
          variant="solid"
          onPress={handleDelete}
        >
          Delete User
        </Button>
      </div>
    </div>
  )
}
